import { LogoMark } from "@assets/LogoMark";
import { LanguageSelector } from "@components/LanguageSelector";
import { UserAvatar } from "@components/UserAvatar";
import { authClient } from "@features/auth/authClient";
import { useUserInfo } from "@features/auth/useAuth";
import { T } from "@i18n/T";
import { useI18nContext } from "@i18n/i18n-react";
import { cn } from "@shadcn/lib/utils";
import { Button } from "@shadcn/ui/button";
import { Separator } from "@shadcn/ui/separator";
import { Link, useMatches, useNavigate, useRouter } from "@tanstack/react-router";
import { CircleHelp, Menu, X, ChevronDown, MapPin } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { getHubRouteFilterService, type HubFilterService as Service } from "./HubMap/hubFilters";
import { HubMap } from "./HubMap/HubMap";
import { useHubSession } from "@contexts/HubSessionContext";
import { useHelp } from "~/contexts/HelpContext";

interface AppHeaderProps {
	location?: {
		address: string;
	};
	hubMetaName?: string;
	service?: Service | "ALL";
	showHelpButton?: boolean;
	showHubLabel?: boolean;
	menuPosition?: "left" | "right";
}

type NavItem = {
	label: string;
	to?: "/" | "/support";
	isActive?: boolean;
	onClick?: () => Promise<void> | void;
};

const baseNavItems = (i18n: ReturnType<typeof useI18nContext>["LL"]["new-ui"]): [NavItem, NavItem] => [
	{
		to: "/",
		label: i18n.HOME(),
	},
	{
		to: "/support",
		label: i18n.CHAT_SUPPORT(),
	},
];

type AppHeaderMenuProps = {
	currentPath: string;
	i18n: ReturnType<typeof useI18nContext>["LL"]["new-ui"];
	isOpen: boolean;
	menuItems: NavItem[];
	onClose: () => void;
	onSignOut: () => Promise<void>;
	user: ReturnType<typeof useUserInfo>;
};

const AppHeaderMenu = (props: AppHeaderMenuProps) => {
	const { currentPath, i18n, isOpen, menuItems, onClose, onSignOut, user } = props;
	if (!isOpen) {
		return null;
	}

	return (
		<div className="fixed inset-0 top-14 z-30 bg-transparent" onClick={onClose}>
			<div className="border-border bg-accent text-neutral-900" onClick={(event) => event.stopPropagation()}>
				<div className="px-2 pt-2 pb-4">
					<div className="px-2 pb-2">
						<LanguageSelector triggerClassName="h-9 w-full justify-center gap-1.5 rounded-[10px] border-foreground/20 bg-transparent text-center font-breit shadow-none [&_[data-slot=select-value]]:justify-center" />
					</div>

					<div className="flex flex-col">
						{menuItems.map((item) => {
							const isActive = item.to ? (item.to === "/" ? currentPath === "/" : currentPath.startsWith(item.to)) : item.isActive;

							if (item.to) {
								return (
									<Link
										key={item.label}
										to={item.to}
										className={cn("rounded-md px-2.5 py-2 text-[1.05rem] leading-8 font-breit", {
											"text-primary font-medium": isActive,
											"font-normal": !isActive,
										})}
										onClick={onClose}
									>
										{item.label}
									</Link>
								);
							}

							return (
								<button
									key={item.label}
									type="button"
									className={cn("rounded-md px-2.5 py-2 text-left text-[1.05rem] leading-8 font-breit", {
										"text-primary font-medium": isActive,
										"font-normal": !isActive,
									})}
									onClick={async () => {
										onClose();
										await item.onClick?.();
									}}
								>
									{item.label}
								</button>
							);
						})}
					</div>
				</div>

				<Separator />

				<div className="p-2">
					{user ? (
						<>
							<div className="flex items-center gap-3 rounded-md px-2 py-2">
								<UserAvatar user={user} className="size-10" />
								<div className="min-w-0">
									<p className="truncate text-base leading-6 font-medium">{user.name || user.email}</p>
									<p className="truncate text-sm leading-5 text-neutral-700">{user.email}</p>
								</div>
							</div>

							<button
								type="button"
								className="block rounded-md px-2 py-2 text-left text-[1.05rem] leading-8 font-breit font-normal"
								onClick={onSignOut}
							>
								<T>{i18n.NAV_SIGN_OUT()}</T>
							</button>
						</>
					) : (
						<Link to="/auth" className="block rounded-md px-2 py-2 text-[1.05rem] leading-8 font-breit font-normal" onClick={onClose}>
							<T>{i18n.NAV_SIGN_IN()}</T>
						</Link>
					)}
				</div>
			</div>
		</div>
	);
};

export const AppHeader = (props: AppHeaderProps) => {
	const { service = "ALL", showHelpButton = false, showHubLabel = true, menuPosition = "right" } = props;
	const i18n = useI18nContext().LL["new-ui"];
	const router = useRouter();
	const navigate = useNavigate();
	const user = useUserInfo();
	const { hub, setHub } = useHubSession();
	const { openIntercom } = useHelp();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isHubMapOpen, setIsHubMapOpen] = useState(false);
	const matches = useMatches();
	const currentPath = router.state.location.pathname;
	const matchedHubMetaName = [...matches]
		.reverse()
		.map((match) => (match.params as { hubMetaName?: string }).hubMetaName)
		.find((hubMetaName): hubMetaName is string => Boolean(hubMetaName));
	const currentHubMetaName = props.hubMetaName ?? matchedHubMetaName ?? hub?.name ?? null;
	const [homeItem, supportItem] = baseNavItems(i18n);
	const hubHomePath = hub ? `/${hub.name}` : "/";
	const selectionFilterService: Service | null = service !== "ALL" ? service : getHubRouteFilterService(currentPath, matchedHubMetaName);
	const shouldShowHubLabel = showHubLabel && currentHubMetaName !== null && selectionFilterService !== null;
	const hubLabel = currentHubMetaName ? `${currentHubMetaName.toUpperCase()} Hub` : "";
	const menuItems: NavItem[] = [
		homeItem,
		hub
			? {
					label: i18n.HUB_PAGE(),
					to: hubHomePath as "/",
					isActive: currentPath === `/${hub.name}` || currentPath.startsWith(`/${hub.name}/`),
				}
			: {
					label: i18n.HUB_PAGE(),
					onClick: () => {
						setIsHubMapOpen(true);
					},
				},
		supportItem,
	];

	const handleSignOut = async () => {
		setIsMenuOpen(false);
		toast.info(i18n.SIGNED_OUT_SUCCESSFULLY());
		await authClient.signOut();
		await router.invalidate();
	};

	const navigateToSelectedHub = (hubMetaName: string) => {
		if (selectionFilterService === "INSTANT_BUY") {
			navigate({ to: "/$hubMetaName/instant-buy", params: { hubMetaName } });
			return;
		}

		if (selectionFilterService === "SHOP_N_COLLECT") {
			navigate({ to: "/$hubMetaName/shop", params: { hubMetaName } });
			return;
		}

		if (selectionFilterService === "RAFFLE") {
			navigate({ to: "/$hubMetaName/raffle", params: { hubMetaName } });
			return;
		}

		// Goods reception shares the Lock B2B filter with Swap: stay in goods reception.
		if (currentPath.includes("/warenannahme")) {
			navigate({ to: "/$hubMetaName/warenannahme", params: { hubMetaName } });
			return;
		}

		if (selectionFilterService === "LOCK_B2B") {
			navigate({ to: "/$hubMetaName/swap", params: { hubMetaName } });
			return;
		}

		if (selectionFilterService === "LOCK_B2C") {
			navigate({ to: "/$hubMetaName/lock", params: { hubMetaName } });
			return;
		}

		navigate({ to: "/$hubMetaName", params: { hubMetaName } });
	};

	const menuButton = (
		<Button
			variant="ghost"
			size="icon"
			className={cn("relative transition-colors", {
				"bg-foreground text-accent hover:bg-foreground/90 hover:text-accent": isMenuOpen,
			})}
			aria-label={isMenuOpen ? i18n.CLOSE_MENU() : i18n.OPEN_MENU()}
			onClick={() => setIsMenuOpen((open) => !open)}
		>
			<span
				className={cn("absolute transition-all duration-200", {
					"rotate-90 opacity-0": isMenuOpen,
					"rotate-0 opacity-100": !isMenuOpen,
				})}
			>
				<Menu className="size-5" />
			</span>
			<span
				className={cn("absolute transition-all duration-200", {
					"rotate-0 opacity-100": isMenuOpen,
					"-rotate-90 opacity-0": !isMenuOpen,
				})}
			>
				<X className="size-5" />
			</span>
		</Button>
	);

	return (
		<>
			<div className="relative z-20">
				<nav className="border-border bg-accent border-b text-neutral-900 shadow-xs">
					<div className="relative mx-auto flex h-14 w-full items-center justify-between px-4 lg:w-[calc(100vw-20rem)] lg:min-w-[704px] lg:max-w-[1280px]">
						<div className="flex min-w-9 items-center">
							{menuPosition === "left" ? (
								menuButton
							) : showHelpButton ? (
								<Button variant="outline" size="default" className="h-9 px-4 font-breit font-medium" onClick={openIntercom}>
									{i18n.HELP()}
									<CircleHelp className="size-4" />
								</Button>
							) : (
								<div className="size-9" aria-hidden="true" />
							)}
						</div>

						{shouldShowHubLabel ? (
							<button
								type="button"
								className="absolute left-1/2 flex h-9 -translate-x-1/2 items-center justify-center"
								onClick={() => setIsHubMapOpen(true)}
							>
								<span className="flex max-w-[calc(100vw-8rem)] items-center gap-2 rounded-md px-4 py-2 text-sm leading-5 text-neutral-900">
									<MapPin className="size-4 shrink-0" />
									<span className="truncate font-normal">{hubLabel}</span>
									<ChevronDown className="size-4 shrink-0" />
								</span>
							</button>
						) : (
							<Link to={hubHomePath as "/"} className="absolute left-1/2 flex h-9 -translate-x-1/2 items-center justify-center">
								<LogoMark className="h-5 w-auto" />
							</Link>
						)}

						<div className="flex min-w-9 items-center justify-end">
							{menuPosition === "right" ? (
								menuButton
							) : showHelpButton ? (
								<Button variant="outline" size="default" className="h-9 px-4 font-breit font-medium" onClick={openIntercom}>
									{i18n.HELP()}
									<CircleHelp className="size-4" />
								</Button>
							) : (
								<div className="size-9" aria-hidden="true" />
							)}
						</div>
					</div>
				</nav>

				<AppHeaderMenu
					currentPath={currentPath}
					i18n={i18n}
					isOpen={isMenuOpen}
					menuItems={menuItems}
					onClose={() => setIsMenuOpen(false)}
					onSignOut={handleSignOut}
					user={user}
				/>
			</div>

			<HubMap
				open={isHubMapOpen}
				onOpenChange={setIsHubMapOpen}
				currentHubMetaName={currentHubMetaName}
				filterService={selectionFilterService}
				renderAction={({ id, name }, close) => (
					<Button
						className="w-full"
						onClick={() => {
							setHub({ id, name, side: undefined });
							close();
							navigateToSelectedHub(name);
						}}
					>
						{i18n.CHOOSE_HUB()}
					</Button>
				)}
			/>
		</>
	);
};
